import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { Announcement } from "../../services/announcement/announcement.models";

/**
 * Hands an announcement to the launcher so it is rendered at the app-frame root instead of
 * wherever the caller happens to live.
 *
 * The header inbox used to render the banner/modal itself. That put both of them inside
 * `<nav class="header-shell">`, which is `position: sticky` with `z-index: 1100` — a stacking
 * context. A backdrop at z-index 10050 inside it still loses to the sidebar's `z-index: 1200`,
 * so the left menu stayed lit and clickable over the overlay, and a top banner was clipped
 * behind it. Mounting from the root is what makes the z-index mean what it says.
 */
@Injectable({ providedIn: "root" })
export class AnnouncementOverlayService {
  private readonly requested = new BehaviorSubject<Announcement | null>(null);
  private readonly closedSubject = new Subject<void>();

  /** The announcement a caller asked the launcher to display, or null. */
  readonly requested$: Observable<Announcement | null> = this.requested.asObservable();

  /** Emits when that announcement is dismissed, so the opener can refresh itself. */
  readonly closed$: Observable<void> = this.closedSubject.asObservable();

  open(announcement: Announcement): void {
    this.requested.next(announcement);
  }

  close(): void {
    if (!this.requested.value) {
      return;
    }
    this.requested.next(null);
    this.closedSubject.next();
  }
}
